/**
 * Admin Route Guard
 *
 * Restricts route access to users with admin privileges.
 * Awaits viewer data (which may not be loaded yet on page refresh)
 * before checking permissions via the typed `canManageSomething()` API.
 *
 * History: this guard previously used `canDo('*') || canDo('admin:*')`
 * which only matches users with the LITERAL `'*'` or `'admin:*'` action.
 * Real admin users don't hold those — they hold `customer:manage`,
 * `space:manage`, etc. The legacy `isAdmin` was a permissive substring
 * scan (matched `*admin*` / `*manage*` / `facility:analytics` in any
 * action). `canManageSomething()` replaces both with a concrete predicate
 * on admin-class action shapes.
 *
 * Per-route action mapping is the strictly-correct long-term shape; this
 * coarse gate is a deliberate compromise so rail visibility, route guard,
 * and profile-panel "Admin" entry all answer the same question.
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { usesBearerTokenAuth } from '../auth-mode';
import { ProfileDataService } from '../services/profile-data.service';
import { SessionService } from '../services/session.service';
import { TeamsService } from '../services/teams.service';
import { ViewerService } from '../services/viewer.service';
import { isAdminPreview } from './admin-preview';
import { tryRefreshSession } from './auth.guard';
import { regionGateActive } from '../region';
import { isServerUnreachable, SERVER_UNREACHABLE_PATH } from './server-unreachable';

export const adminGuard: CanActivateFn = async (route, state) => {
  // Data-residency gate (desktop): no backend calls until a region is picked.
  if (regionGateActive()) return false;

  // Dev-only offline preview of the admin shell (see admin-preview.ts).
  if (isAdminPreview(route)) return true;

  const viewerService = inject(ViewerService);
  const profileData = inject(ProfileDataService);
  const session = inject(SessionService);
  const teamsService = inject(TeamsService);
  const router = inject(Router);
  await viewerService.ensureLoaded();
  let allowed = profileData.canManageSomething();

  // The viewer is cached for the session. If it was loaded with an expired
  // access token the viewer query fails silently and we cache a null/partial
  // viewer — so a user whose token expired while sitting in /admin would be
  // ejected to /home with no refresh attempt. Before denying, try the same
  // refresh path authGuard uses, force a fresh viewer load, and re-check.
  // This only runs on the would-be-denied path, so it can never regress the
  // previous behaviour (which bounced unconditionally).
  if (!allowed) {
    if (usesBearerTokenAuth(teamsService.isInTeams())) {
      await tryRefreshSession();
    } else {
      await session.refreshAccessToken();
    }
    await viewerService.reload();
    allowed = profileData.canManageSomething();
  }

  if (!allowed && isServerUnreachable(viewerService.loadError())) {
    // No viewer because the server could not be reached is no answer about
    // the account: say so and offer Try again. Never "No admin
    // access" with Sign out, which during an outage invites an admin to
    // sign out.
    // eslint-disable-next-line no-console
    console.warn('[adminGuard] could not reach the server:', viewerService.loadError()?.code);
    void router.navigate([SERVER_UNREACHABLE_PATH], { queryParams: { returnUrl: state?.url ?? null } });
    return false;
  }

  if (!allowed) {
    const actions = profileData.actionsSnapshot();
    // eslint-disable-next-line no-console
    console.warn(
      '[adminGuard] DENIED — viewer holds no admin-class action.',
      {
        actionsCount: actions.length,
        sampleActions: actions.slice(0, 15),
        canDoStar: profileData.canDo('*'),
        canDoAdminStar: profileData.canDo('admin:*'),
      },
    );
    // Send a signed-in non-admin to the ungated /no-access surface. NOT
    // /home-preview: that route does not exist in this admin-only app, so it
    // would fall through the layout '**' back to /overview — which is under
    // this same guard — and deny again in an infinite loop (bd 7p4).
    // /no-access lives outside the admin layout, so the denial terminates.
    void router.navigate(['/no-access']);
  } else if (typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.debug('[adminGuard] ALLOWED — canManageSomething() = true');
  }
  return allowed;
};
